import { useQuery } from 'react-query';
import type { KeywordSuggestionHit, CategorySuggestionHit } from '@jbhi-fi/apis/src/googleSuggestions';
import type { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { buildFilterExpression } from '@jbhi-fi/apis/src/utils/filterExpression';
import type { FacetFilter } from '@jbhi-fi/apis/src/googleSearch/types';
import { getSearchClient, getSuggestionsClient } from './client';

export type { KeywordSuggestionHit, CategorySuggestionHit };

export type UseGoogleSuggestionsResult = {
  keywords: Array<KeywordSuggestionHit>;
  categories: Array<CategorySuggestionHit>;
  nbSaleItems: number;
  isLoading: boolean;
};

export const useGoogleSuggestions = (query: string, isEnabled = true): UseGoogleSuggestionsResult => {
  const { data, isLoading } = useQuery(
    ['googleCompleteQuery', query],
    async () => {
      const response = await getSuggestionsClient().completeQuery({ query });
      return {
        keywords: response.keywords ?? [],
        categories: response.categories ?? [],
        nbSaleItems: response.nbSaleItems ?? 0,
      };
    },
    {
      enabled: isEnabled && query.length >= 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );

  return {
    keywords: data?.keywords ?? [],
    categories: data?.categories ?? [],
    nbSaleItems: data?.nbSaleItems ?? 0,
    isLoading,
  };
};

export type UseGoogleQuickProductsResult = {
  hits: Array<AlgoliaProductHit>;
  nbHits: number;
  priceRange: { min: number; max: number };
  facets: Record<string, Record<string, number>>;
  attributionToken?: string;
  filter?: string;
  isLoading: boolean;
  isFetching: boolean;
};

export type UseGoogleQuickProductsOptions = {
  priceRange?: { min: number; max: number };
  isSaleItems?: boolean;
};

const buildQuickProductsFilter = (options?: UseGoogleQuickProductsOptions): string | undefined => {
  const parts: Array<string> = [];

  if (options?.priceRange) {
    const { min, max } = options.priceRange;
    const facets: Array<FacetFilter> = [];
    if (min > 0 || Number.isFinite(max)) {
      facets.push({ name: 'Price', value: [min, max] });
    }
    const expr = buildFilterExpression(facets);
    if (expr) parts.push(expr);
  }

  if (options?.isSaleItems) parts.push('attributes.onPromotion: ANY("true", "True", "1")');

  return parts.length ? parts.join(' AND ') : undefined;
};

export const useGoogleQuickProducts = (
  query: string,
  hitsPerPage = 4,
  isEnabled = true,
  options?: UseGoogleQuickProductsOptions
): UseGoogleQuickProductsResult => {
  const filter = buildQuickProductsFilter(options);

  const { data, isLoading, isFetching } = useQuery(
    ['googleQuickProducts', query, hitsPerPage, filter ?? ''],
    async () => {
      const response = await getSearchClient().search({
        query,
        pageSize: hitsPerPage,
        ...(filter ? { filter } : {}),
      });
      const priceStats = response.facetsStats?.['Price'] ?? response.facetsStats?.['facets.Price'];
      return {
        hits: response.hits,
        nbHits: response.totalSize,
        priceRange: { min: priceStats?.min ?? 0, max: priceStats?.max ?? 0 },
        facets: response.facets ?? {},
        attributionToken: response.attributionToken,
      };
    },
    {
      enabled: isEnabled && query.length >= 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );

  return {
    hits: data?.hits ?? [],
    nbHits: data?.nbHits ?? 0,
    priceRange: data?.priceRange ?? { min: 0, max: 0 },
    facets: data?.facets ?? {},
    attributionToken: data?.attributionToken,
    filter,
    isLoading,
    isFetching,
  };
};

export const useGoogleRecentlyViewed = (
  skus: Array<number>,
  isEnabled = true
): { hits: Array<AlgoliaProductHit>; attributionToken?: string; isLoading: boolean } => {
  const { data, isLoading } = useQuery(
    ['googleRecentlyViewed', skus.join(',')],
    async () => {
      const response = await getSearchClient().search({
        featuredProductIds: skus.map(String),
        pageSize: skus.length,
      });
      return { hits: response.featuredProducts, attributionToken: response.attributionToken };
    },
    {
      enabled: isEnabled && skus.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    }
  );

  return { hits: data?.hits ?? [], attributionToken: data?.attributionToken, isLoading };
};
